import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { configureApi } from '@posilka/client/api/client';
import { App } from './App';
import { detectLanguage, initI18n } from './i18n';
import { getLaunchParams, initTelegram, insideTelegram } from './lib/telegram';
import './index.css';

const LAUNCH_PARAMS_KEY = 'posilka-launch-params';

/**
 * Telegram launch params URL hash'ida keladi (`#tgWebAppData=...`). SPA ichida boshqa sahifaga
 * o'tilgach hash yo'qoladi; sahifa qayta yuklansa (dev'da Vite reload) SDK ularni topolmaydi —
 * shuning uchun saqlab, kerak bo'lsa tiklaymiz.
 */
function preserveLaunchParams() {
  try {
    const { hash, pathname, search } = window.location;
    if (hash.includes('tgWebAppData')) {
      sessionStorage.setItem(LAUNCH_PARAMS_KEY, hash);
    } else {
      const saved = sessionStorage.getItem(LAUNCH_PARAMS_KEY);
      if (saved)
        window.history.replaceState(window.history.state, '', `${pathname}${search}${saved}`);
    }
  } catch {
    // sessionStorage yo'q — SDK o'zi urinib ko'radi.
  }
}

class OutsideTelegramError extends Error {
  override name = 'OutsideTelegramError';
}

configureApi({ baseUrl: import.meta.env.VITE_API_URL });

async function bootstrap() {
  // Prod: oddiy brauzerda ochilgan — Telegram SDK'ni ishga tushirishga urinmaymiz.
  if (!import.meta.env.DEV && !insideTelegram()) throw new OutsideTelegramError();
  preserveLaunchParams();
  if (import.meta.env.DEV) await (await import('./lib/mockEnv')).mockEnvIfNeeded();
  await initTelegram(import.meta.env.DEV);

  // Boshlang'ich til Telegram'dan; kirgandan keyin server tili qo'llanadi (AuthGate).
  await initI18n(detectLanguage(getLaunchParams().tgWebAppData?.user?.language_code));

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

/** Render'gacha yiqilsa — qora ekran o'rniga xabar (Telegram WebView'da konsol yo'q). */
function showFatal(error: unknown) {
  console.error('[posilka] bootstrap failed', error);
  const root = document.getElementById('root');
  if (!root) return;
  root.innerHTML = '';
  const box = document.createElement('div');
  box.style.cssText =
    "min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;padding:24px;text-align:center;font:15px 'Manrope Variable',system-ui";

  // Telegram tashqarisida (oddiy brauzerda) ochilgan — texnik xato emas, botga yo'naltiramiz.
  const outsideTelegram = error instanceof OutsideTelegramError;
  const bot = import.meta.env.VITE_BOT_USERNAME;
  const ru = navigator.language.startsWith('ru');
  const title = document.createElement('p');
  title.style.fontSize = '17px';
  title.textContent = outsideTelegram
    ? ru
      ? "📦 Jo'natmaBor работает внутри Telegram"
      : "📦 Jo'natmaBor Telegram ichida ishlaydi"
    : ru
      ? 'Не удалось запустить приложение'
      : "Ilovani ishga tushirib bo'lmadi";
  box.append(title);

  if (outsideTelegram && bot) {
    const link = document.createElement('a');
    link.href = `https://t.me/${bot}`;
    link.textContent = ru ? `Открыть @${bot}` : `@${bot} ni ochish`;
    link.style.cssText =
      'background:#c6ff3d;color:#0b0f14;padding:12px 22px;border-radius:999px;font-weight:700;text-decoration:none';
    box.append(link);
  } else if (!outsideTelegram) {
    const detail = document.createElement('p');
    detail.style.cssText = 'color:var(--muted);font-size:12px;white-space:pre-wrap';
    detail.textContent = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    box.append(detail);
  }
  root.append(box);
}

bootstrap().catch(showFatal);
